<script setup lang="ts">
  import { _ } from 'lodash'
  import ProjectSideBar from '@/components/projectDetail/ProjectSideBar.vue'
  import GTLPMap from '@/components/projectDetail/GTLPMap/GTLPMap.vue'
  import type { BaseStoreType } from '@/types/storeTypes'
  import type { SectionLine, SectionLineUploadCollection } from '@/stores/type/sectionLine.type'
  import type {
    SectionSelectionPolygon,
    SelectionPolygonUploadCollection
  } from '@/stores/type/selectionPolygon.type'
  import type { GIPointCollection, GroundInvestigationPoint } from '@/stores/type/giPoint.type'
  import { useEventsBus } from '@haskoning/vue-component-library'
  import { useAuthorizationStore } from '@/stores/authorization'
  import { useDjangoAuthStore } from '@/stores/authDjango'
  import { resetAllStores } from '@/utils/pinia'
  import ProfileGraphContainer from '@/components/projectDetail/SectionLineVisualisation/ProfileGraphContainer.vue'
  import { useSelectedVisualizationColumnStore } from '@/stores/selectedVisualizationColumnStore'

  const route = useRoute()
  const store = useProjectStore()
  const giPointStore = useGIPointsStore()
  const giSetStore = useGIDataUploadCollectionStore()
  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const sectionLinesStore = useSectionLinesStore()
  const selectionPolygonStore = useSelectionPolygonsStores()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
  const rasterFileUploadStore = useRasterFileUploadStore()
  const boreholeSampleObservationStore = useBoreholeSampleObservationStore()
  const projectUserStore = useProjectUserStore()
  const authorizationStore = useAuthorizationStore()
  const selectedVisualizationColumnStore = useSelectedVisualizationColumnStore()
  const authStore = useDjangoAuthStore()

  const { currentItem: currentProject, isLengthProfileGraphBarShown } = storeToRefs(store)
  const { items: projectUsers } = storeToRefs(projectUserStore)
  const { user: currentUser } = storeToRefs(authStore)
  const { emit } = useEventsBus()
  const internalDisplay = ref(false)
  const fetchRelatedItems = async () => {
    resetAllStores()
    store.setProjectLoading(true)
    try {
      const currentProjectId = Number(route.params.id)
      await store.fetchItem(currentProjectId)
      await sectionLineUploadCollectionsStore.fetchItems({
        search: currentProjectId
      })
      await giSetStore.fetchItems({ search: currentProjectId })
      await sectionLinesGIPointsStore.fetchItems({})
      await selectionPolygonStore.fetchOptions()
      await sectionLineUploadCollectionsStore.fetchOptions()
      await rasterFileUploadStore.fetchItems({ search: currentProjectId })
      await boreholeSampleObservationStore.fetchItems({ search: currentProjectId })
      await selectedVisualizationColumnStore.fetchItems({
        project_user__project_id: currentProjectId
      })
      await projectUserStore.fetchItems({ search: currentProjectId })
      await authorizationStore.fetchItems({})

      const currentProjectUser = projectUsers.value.find(
        (item) => item.user === currentUser?.value?.id
      )
      authorizationStore.setCurrentItem([currentProjectUser?.role])

      sectionLineUploadCollectionsStore.addHiddenFieldToItems()
      addChildrenItems(sectionLineUploadCollectionsStore, sectionLinesStore, 'sectionline_set')
      addChildrenItems(giSetStore, giPointStore, 'gipoint_set')
      addChildrenItems(
        sectionLinesStore,
        selectionPolygonStore,
        'properties.sectionselectionpolygon_set'
      )
    } catch (error) {
      console.error('Failed to fetch related items: ', error)
    } finally {
      store.setProjectLoading(false)
      emit('project-loaded')
    }
  }

  const addChildrenItems = (
    parentStore: BaseStoreType<
      | SectionLineUploadCollection
      | SelectionPolygonUploadCollection
      | GIPointCollection
      | SectionLine
    >,
    childStore: BaseStoreType<SectionLine | SectionSelectionPolygon | GroundInvestigationPoint>,
    childrenFeatureKey: string
  ) => {
    childStore.setItems([]) // clear items before appending
    parentStore.items.forEach((item: any) => {
      const features = _.get(item, childrenFeatureKey + '.features')
      if (features) {
        childStore.addItems(features)
      }
    })
  }

  onMounted(async () => {
    if (!currentProject.value || _.isEmpty(currentProject.value)) {
      await fetchRelatedItems()
    }
    //TODO: currently, there is a layout shift when the sidebar is loaded before the RHDHVHeader,
    // so we need to delay the sidebar loading
    internalDisplay.value = true
  })
</script>
<template>
  <div class="h-100 d-flex overflow-hidden">
    <ProjectSideBar v-if="internalDisplay" />
    <div class="d-flex flex-column w-100 h-100 overflow-hidden">
      <ProfileGraphContainer v-if="isLengthProfileGraphBarShown" />
      <GTLPMap />
    </div>
  </div>
</template>

<style scoped></style>
